import React from 'react';
import {useSpring} from 'react-spring';

import KeyboardArrowDownIcon from '@edna/icons/keyboardArrowDown.svg';

import {useTheme} from '../styles';
import {easing, useMeasure} from '../utils';
import * as S from './style';

export type TProps = {
  className?: string;
  open: boolean;
  disabled?: boolean;
  toggleOpen: TEmptyFunction;
  label?: React.ReactNode;
  rightLabel?: React.ReactNode;
  children: React.ReactNode;
  arrowIcon?: TAnyComponent;
  isTopAlign?: boolean;
  labelComponent?: TAnyComponent;
  animationDuration?: number;
};

const DEFAULT_MAX_HEIGHT = 2000;

const Collapse = React.memo<TProps>(
  ({
    open,
    label,
    rightLabel,
    disabled,
    children,
    className,
    isTopAlign,
    toggleOpen,
    animationDuration,
    arrowIcon: ArrowIcon = KeyboardArrowDownIcon,
    labelComponent: LabelComponent = S.Label,
  }) => {
    const [childrenRef, {height: childrenHeight}] = useMeasure<HTMLDivElement>();
    const theme = useTheme();

    const styles = useSpring({
      to: [
        {
          maxHeight: open ? childrenHeight || DEFAULT_MAX_HEIGHT : 0,
          opacity: open ? 1 : 0,
          overflow: open ? 'visible' : 'hidden',
        },
      ],
      from: {maxHeight: 0, opacity: 0},
      update: {maxHeight: childrenHeight, config: {duration: 0}},
      config: {
        easing,
        duration: animationDuration ?? theme.animation.duration,
      },
    });

    const handleToggleOpen = React.useCallback(() => {
      if (!disabled) {
        toggleOpen();
      }
    }, [disabled, toggleOpen]);

    return (
      <div className={className}>
        <LabelComponent
          open={open}
          onClick={handleToggleOpen}
          isTopAlign={isTopAlign}
          disabled={disabled}
        >
          {label}
          <S.ArrowIcon open={open} as={ArrowIcon} />
          {rightLabel}
        </LabelComponent>
        <S.AnimatedChildren style={styles}>
          <div ref={childrenRef}>{children}</div>
        </S.AnimatedChildren>
      </div>
    );
  },
);

export default Collapse;

export const CollapseLabel = S.Label;

type TStatefulCollapse = Omit<TProps, 'open' | 'toggleOpen'> & {
  isOpenDefault?: boolean;
};

export const StatefulCollapse = React.memo<TStatefulCollapse>(
  ({isOpenDefault = false, disabled, ...props}) => {
    const [open, setOpen] = React.useState(isOpenDefault);
    const toggleOpen = React.useCallback(() => {
      if (!disabled) {
        setOpen((prevState) => !prevState);
      }
    }, [disabled]);

    return <Collapse {...props} open={open} toggleOpen={toggleOpen} disabled={disabled} />;
  },
);
